import type {
  BulkUploadTemplates,
  RiskGroupsMappings,
} from '@/components/molecules/bulk-order-upload/types'
import type { ValidRfqType } from '@/trading/forms/rfq-form/types'
import type { ObjectiveConfig } from '@/types/api/objective-params'

import { ENABLE_SECURITIZED_VIEW } from '@moment/marketplace-feature/feature-flags'
import { ENABLE_SIMPLIFIED_ORDER_ENTRY } from '@moment/order-entry-feature/feature-flags'

// Quant
export const IS_ATTESTATION_AUTHORING_ENABLED =
  'ff.is_attestation_authoring_enabled'

// Gates authoring only: a saved control's marketplace action and "no order
// action" still render and round-trip for everyone.
export const IS_MARKETPLACE_RESTRICTION_AUTHORING_ENABLED =
  'ff.frontend_platform.risk_controls.marketplace_restriction'

export const IS_EXTERNAL_QUOTES_UPLOAD_ENABLED = 'ff.external_quotes_upload'

export const IS_STRATEGIES_ENABLED = 'is_strategies_enabled'
export const IS_PORTFOLIOS_REBALANCING_V2_ENABLED =
  'ff.is_portfolios_rebalancing_v2_enabled'
// Starts holdings uploads as new_money proposals (constraint_builder agent,
// standard proposal chrome) instead of rebalancing ones. Off keeps today's
// rebalancer behaviour, so no org needs backfilling.
export const HOLDINGS_UPLOAD_NEW_MONEY_MODE =
  'ff.holdings_upload_new_money_mode'
export const LINK_ACCOUNTS_PROPOSAL_BUILDER =
  'ff.link_accounts_proposal_builder'
export const PROPOSAL_BUILDER_V2_SHOW_MUNICIPAL_ATTRIBUTES =
  'proposal_builder_v2_show_municipal_attributes'
export const PORTFOLIO_BUILDER_MAX_SELL_CONSTRAINT =
  'ff.portfolio_builder_max_sell_constraint'
// Account-level view in the proposal builder: account/sleeve switcher in the
// header plus account-wide statistics, holdings, and risk factors.
export const IS_PORTFOLIO_ACCOUNT_VIEW_ENABLED =
  'ff.portfolios.frontend_platform_.portfolio_account_view'
export const HIDE_ALLOCATION_TOOL = 'ff.hide_allocation_tool'
export const ENABLE_AOMS_RISK_GROUP_MISMATCH_ENFORCEMENT =
  'ff.frontend_platform.aoms_risk_group_mismatch_enforcement'
export const IS_PROPOSAL_VERSION_HISTORY_ENABLED =
  'ff.is_proposal_version_history_enabled'
// The full help center: the navbar footer entry and every /help route.
export const IS_HELP_CENTER_ENABLED = 'ff.is_help_center_enabled'
// Opens /help to the link-only release (direct article links only) without the
// navbar entry.
export const HELP_CENTER_TB2_ARTICLE = 'ff.help_center_tb2_article'

// Either gate opens /help; which routes exist is decided by the bundled content.
export const HELP_CENTER_ACCESS_GATES = [
  IS_HELP_CENTER_ENABLED,
  HELP_CENTER_TB2_ARTICLE,
]
// Gate on the outstanding-attestation prompt. Owned by public-api, which reads
// the same gate to block the AI copilot routes for a user who has not
// acknowledged; both sides must flip together or the UI prompts nobody while
// the API rejects everybody.
export const ATTESTATION_ENFORCEMENT_ENABLED =
  'ff.public_api.attestation_enforcement'
export const IS_AI_ENABLED = 'is_ai_enabled'
export const IS_AI_OPUS_FAST_ENABLED = 'ff.is_ai_opus_fast_enabled'
// Shows the Partner (coordinator agent) nav entry. Layers on the
// `automation::read` permission.
export const IS_PARTNER_ENABLED = 'ff.is_partner_enabled'
// Exposes the AI Controls page (`/ai/controls`) and its entry points, so an org
// can have the AI chat without the org-wide agent-instruction and skills
// configuration. Layers on top of IS_AI_ENABLED rather than replacing it, and
// must be on for every org expected to keep Controls.
export const SHOW_AI_CONTROLS = 'ff.show_ai_controls'
// Hides the standalone proposal list page (`/proposals`), redirecting to the
// Views hub (`/views?entity=proposal`). Enable only for cohorts that have the
// Views hub, since that is the redirect target.
export const HIDE_PROPOSAL_LIST_PAGE = 'ff.hide_proposal_list_page'
// Offers the projected-cash pivot columns in the client list's column picker.
// Separate from horizon's ff.horizon.sorm_projected_cash_enabled, which gates
// the SORM fields themselves, so the UI can roll out after the data.
export const IS_PROJECTED_CASH_PIVOT_ENABLED =
  'ff.portfolios.frontend_platform.projected_cash_fe'
export const IS_PROPOSAL_CURRENCY_SELECTOR_ENABLED =
  'is_proposal_currency_selector_enabled'
export const DISABLE_SECURITY_LEVEL_SLEEVE_ASSIGNMENT =
  'disable_security_level_sleeve_assignment'
export const PROPOSAL_BUILDER_EXPORT_FOR_PORTFOLIO_SOLUTIONS =
  'proposal_builder_export_for_portfolio_solutions'
export const IS_VIEWS_LOT_EDITING_ENABLED = 'ff.is_views_lot_editing_enabled'
// Drag-to-reorder view cards on the Views grid; persists via saved-search-layout.
export const IS_VIEWS_DRAG_REORDER_ENABLED = 'ff.is_views_drag_reorder_enabled'
export const ENABLE_VIEWS_SORM_GRID = 'ff.frontend_platform.views.sorm_grid'
// Hot-swaps the account Proposals tab from the clients table to the SORM grid.
// Off keeps the clients table, including its saved column layout.
export const ENABLE_ACCOUNT_PROPOSALS_SORM_GRID =
  'ff.frontend_platform.account_details.proposals.sorm_grid'
export const IS_EXPORT_EFFECTIVE_MARKUP_TO_CSV_ENABLED =
  'ff.is_export_effective_markup_to_csv_enabled'
// Gates the cash-raises "Withdrawn" column (persisted `amount_raised`), the
// progress ring on the v3 cash-raise cards (FIPM-609), and switches the
// funding sleeve's reserved-for-cash-raise figure from the full requested
// amount to requested minus already-withdrawn. Bare gate name to match the
// existing Statsig gate; new flags should use the ff. prefix.
export const MOA_CRR_SHOW_WITHDRAWN = 'moa_crr_show_withdrawn'
export const IS_STRATEGY_ENTITY_ENABLED = 'ff.is_strategy_views_entity_enabled'
// Resolves uploaded security-list identifiers through SORM AllReferenceData instead of Live Inventory.
export const IS_SECURITY_LIST_UPLOAD_VIA_SORM_ENABLED =
  'ff.security_list_upload_via_sorm'
// Serves instrument search typeahead from SORM AllReferenceData instead of Live Inventory.
export const IS_INSTRUMENT_SEARCH_VIA_SORM_ENABLED =
  'ff.instrument_search_via_sorm'
// Exposes allocation templates (SORM AssetAllocationTemplate) in the Views hub
// and adds a nav entry under Strategies. The same Statsig flag also gates
// server-side SORM introspection in horizon, so flipping this is the single
// source of truth for the rollout. Access is bounded separately by the
// `allocation_template::read` permission, which horizon enforces on /query too.
export const IS_ALLOCATION_TEMPLATE_ENTITY_ENABLED =
  'ff.horizon.sorm_asset_allocation_template_entity_enabled'
// Assign an asset allocation template to an account (create assignment +
// render the account's sleeves as the assignment's materialized tree).
export const IS_ALLOCATION_TEMPLATE_ACCOUNT_ASSIGNMENT_ENABLED =
  'ff.frontend_platform.allocation_template_account_assignment'
// Accounts' own gate for refusing sleeve writes that break the allocation
// sum, read here so the warning shows only where the refusal is real. It
// matches here only through rules keyed on the `organizationID` custom field:
// the web user's ID is not the org ULID accounts evaluates it with.
export const IS_SLEEVE_ALLOCATION_VALIDATION_ENABLED =
  'ff.accounts.sleeve_allocation_validation_enabled'
// Same Statsig gate horizon uses to advertise AccountGroup in SORM.
export const IS_ACCOUNT_GROUP_ENTITY_ENABLED =
  'ff.horizon.sorm_account_group_entity_enabled'
export const IS_EXPORT_BESPOKE_SUMMARY_ENABLED =
  'ff.is_export_bespoke_summary_enabled'
// Gates the bulk proposal actions in Views — reject, zero trades, liquidate,
// lock, reset, send to blotter, and send to optimizer — every one of which runs
// through its Horizon Action. Off leaves the dock and its menus as they were.
export const ENABLE_BULK_PROPOSAL_ACTIONS =
  'ff.portfolios.frontend_platform.bulk_proposal_actions'

// Gates the Optimize action on the trade plan's bulk-action bar, which starts a
// server-owned optimization run over the checked sleeves. Only this button starts
// one, so the run machinery behind it stays idle while the flag is off.
export const ENABLE_TRADE_PLAN_OPTIMIZATION =
  'ff.portfolios.frontend_platform.trade_plan_optimization'
// Gates advisor trade plans: the security-first matrix with its Accounts
// sidebar, and the switch back to the account-first planner.
export const ENABLE_ADVISOR_TRADE_PLANS =
  'ff.portfolios.frontend_platform.advisor_trade_plans'
// Gates the "Optimize - Minimization" (minimize-deployed-cash) nav action.
export const IS_MINIMIZE_DEPLOYED_CASH_OPTIMIZATIONS_ENABLED =
  'ff.is_minimize_deployed_cash_optimizations_enabled'
// Same gate suboptimal checks before reading custom fields from SORM.
export const ENABLE_PROPOSAL_CUSTOM_FIELDS =
  'ff.portfolios.fi_reference_data_from_sorm'
// Offers "Optimize households together" in the create-proposal dialog's
// Advanced section, which routes the proposal to propman's household optimizer.
export const HOUSEHOLD_OPTIMIZATION = 'ff.portfolios.household_optimization'

// Reports
// Gates every `/reports` route: home, builder and export.
export const REPORTS_V2 = 'ff.reports_v2'

// Trading
export const ENABLE_TRADER_ORDER_BUYING_POWER_CHECKS =
  'ff.trading.frontend_platform.enable_trader_order_buying_power_checks'
export const VIEW_UNMATCHED_EXECUTIONS_ENABLED =
  'ff.trading.frontend_platform.view_unmatched_executions'
export const USER_SETS_HOMEPAGE =
  'ff.trading.frontend_platform.user_sets_homepage'
// Not exported: the IPO/IOI surfaces live in `@moment/blotter-feature`, which
// declares its own copy of the key. Listed here only so the devtools panel offers
// the toggle — the two spellings must stay identical.
const ENABLE_IPO_IOI_ORDER_MANAGEMENT =
  'ff.trading.frontend_platform.ipo_ioi_order_management'
// Not exported: `@moment/blotter-feature` declares and reads its own copy
// (`ENABLE_EXECUTION_PRINCIPAL_ACCOUNT_SELECT`). Listed only for the devtools toggle.
const ENABLE_EXECUTION_PRINCIPAL_ACCOUNT_SELECT =
  'ff.trading.frontend_platform.execution_principal_account_select'
// Not exported: `@moment/blotter-feature` declares and reads its own copy
// (`ENABLE_LOB_SOR_EXECUTION`). Listed only for the devtools toggle.
const ENABLE_LOB_SOR_EXECUTION =
  'ff.trading.frontend_platform.lob_sor_execution'
export const ENABLE_MARGIN_TRADING =
  'ff.trading.frontend_platform.margin_trading'
// Lets advisors mark prime brokerage available for the rest of the day on the
// account details panel, ahead of the custodian's next start-of-day file.
export const ENABLE_PRIME_BROKERAGE_OVERRIDE =
  'ff.trading.frontend_platform.prime_brokerage_override'
export const ENABLE_AUTO_EX_RFQ_DESK_FEES =
  'ff.trading.frontend_platform.enable_auto_ex_rfq_desk_fees'
export const ENABLE_POST_TRADE_ALLOCATION =
  'ff.trading.frontend_platform.enable_post_trade_allocation'
export const ENABLE_BLOTTER_V2 =
  'ff.trading.frontend_platform.enable_blotter_v2'
// Hides the v1 blotter surfaces: the advisor Orders, trade-desk Trade blotter,
// and both Historical orders nav items, plus the Views row-click deep links
// into historical orders. Read through `useIsBlotterV1Hidden`, which also
// requires ENABLE_BLOTTER_V2.
export const DISABLE_BLOTTER_V1 =
  'ff.trading.frontend_platform.disable_blotter_v1'
// Gates blotter v2's order alerts. feature-blotter declares the same key
// (`ENABLE_BLOTTER_ORDER_ALERTS`) and reads it; listed only for the devtools toggle.
const ENABLE_BLOTTER_ORDER_ALERTS =
  'ff.trading.frontend_platform.blotter_order_alerts'
// Unstripes blotter v2's grids. feature-blotter declares the same key
// (`DISABLE_FORCED_STRIPED_ROWS`) and reads it; listed only for the devtools toggle.
const DISABLE_FORCED_STRIPED_ROWS =
  'ff.trading.frontend_platform.disable_forced_striped_rows'
export const ENABLE_FIFO_WITH_AVERAGING =
  'ff.trading.frontend_platform.enable_fifo_with_averaging'
export const ENABLE_REP_FEES = 'ff.trading.frontend_platform.enable_rep_fees'
export const ENABLE_EQUITIES_TRADING_UI =
  'ff.trading.frontend_platform.equities_trading_ui'
export const ENABLE_EXECUTION_ORDER_REPLACE =
  'ff.trading.horizon.execution_order_replace'
// Gates the order details per-execution trade cancel. feature-blotter declares
// the same key (`ENABLE_TRADE_CANCEL`) and reads it; listed only for the devtools toggle.
const ENABLE_TRADE_CANCEL = 'ff.trading.trade_cancel'
// Gates per-row Buy/Sell on fund holdings (asset_type=fund: mutual funds,
// money-market funds, ETFs) in the account-details holdings grid. Deliberately
// holdings-scoped, unlike ENABLE_EQUITIES_TRADING_UI: it has no bearing on
// fund support in the order-entry form, trade blotter, or other surfaces.
export const ENABLE_ACCOUNT_DETAILS_HOLDINGS_FUND_TRADING =
  'ff.trading.frontend_platform.enable_account_details_holdings_fund_trading'
// Reveals the Buy/Sell buttons + order-entry pane on the account-details
// Holdings tab. Visibility-only, targeted per-org independently of
// ENABLE_EQUITIES_TRADING_UI above. Default off.
export const ENABLE_NEW_ACCOUNT_DETAILS_HOLDINGS_ORDER_ENTRY =
  'ff.trading.frontend_platform.enable_new_account_details_holdings_order_entry'
export const ENABLE_EQUITY_UNIVERSE_CONTROLS =
  'ff.trading.frontend_platform.equity_universe_controls'
export const USE_MKTX_RFQ_TIMER_LOOKUP =
  'ff.trading.frontend_platform.use_mktx_rfq_timer_lookup'
// Gates the "Bid Wanted" / "Offer Wanted" option in the order-entry order-type toggle
// (advisor-initiated quote-wanted orders, order_type=quote_wanted). Off = hidden.
// Targeted per-org via the Statsig user's organizationID (derived from the
// authenticated user / JWT session).
export const ENABLE_ADVISOR_QUOTE_WANTED =
  'ff.trading.frontend_platform.enable_advisor_bid_wanted'
// Gates the Actions tab's execution quote card, on top of each order type's own
// gates. Off = no card. feature-blotter declares the same key; read there.
const ENABLE_ORDER_EXECUTION_BEST_QUOTE =
  'ff.trading.frontend_platform.order_execution_best_quote'
// SORM's gate on every `inquiry_*` field of the Order entity, so every inquiry
// flow is read alongside it — without it those fields prune away.
export const ENABLE_INQUIRIES = 'ff.trading.enable_inquiries'
// Adds the v2 blotter's own "Bulk order" Create-menu entry, which opens the
// native upload dialog, and drops the entries that deep-link into v1's
// OneSchema importer for anyone it's offered to.
export const ENABLE_BULK_UPLOAD_V2 =
  'ff.trading.frontend_platform.bulk_upload_v2'
// Hides the blotter Create menu's "Order(s)", the entry into full-page order
// entry at /order-entry; simplified order entry hides it too. The page itself
// stays reachable by URL.
export const HIDE_FULL_PAGE_ORDER_ENTRY =
  'ff.trading.frontend_platform.hide_full_page_order_entry'
// Not exported: `@moment/blotter-feature` declares and reads its own copy
// (`ENABLE_BULK_SEND_LIST_RFQS`). Listed only for the devtools toggle.
const ENABLE_BULK_SEND_LIST_RFQS =
  'ff.trading.frontend_platform.bulk_send_list_rfqs'
// Trade Blotter v2 launch callout on /blotter. Independent of
// IS_CHANGELOG_WIDGET_ENABLED; target the same orgs as ENABLE_BLOTTER_V2,
// which /blotter itself requires.
export const ENABLE_TB2_LAUNCH_CALLOUT =
  'ff.trading.frontend_platform.tb2_launch_callout'
// Temporary, for OpCo Research tests. On Research: shows the order entry's
// Buy/Sell switch, makes the order limit-only (Limit, no order-type selector)
// and relabels the bond page's Buy buttons "Trade". Off = buy-only Research.
export const ENABLE_OPCO_RESEARCH_TRADE_MODE =
  'ff.trading.frontend_platform.opco_research_trade_mode'
// Owned by horizon, which reads the same gate to enforce trading-group grants
// on trading; the trading-group list follows it so the two cannot disagree.
// Target orgs by the `organizationID` custom field: a `user_id` rule matches
// only the backend, which keys its Statsig user by org.
export const TRADING_GROUP_OBJECT_ACCESS = 'trading_group_object_access'
// Not exported: `@moment/blotter-feature` declares and reads its own copy
// (`ENABLE_ORDER_INSTRUMENT_PANE`). Listed only for the devtools toggle.
const ENABLE_ORDER_INSTRUMENT_PANE =
  'ff.trading.frontend_platform.enable_order_instrument_pane'

// Research
export const IS_NEW_ISSUE_CD_TILE_ENABLED = 'is_new_issue_cd_tile_enabled'
// Off keeps bond discover. On mounts feature-research at /research/discover.
export const RESEARCH_FE = 'ff.frontend_platform.research'
// Turns on the fixed income hub for an org: the advisor task board, its run and
// session pages beside a task rail, its nav entry, and workflow sessions in the
// chat list. Each surface also checks the viewer's `workflow::*` permissions.
export const FIXED_INCOME_HUB = 'ff.frontend_platform.fixed_income_hub'
// Shows the Workflows entity in Views and its session pages. The viewer also
// needs `workflow::read`.
export const WORKFLOWS_VIEW = 'ff.frontend_platform.workflows_view'
// Turns on the /agent chat page and its Agent nav entry. The fixed income hub
// opens /agent too, so the page also renders on FIXED_INCOME_HUB. The viewer
// also needs `workflow::read`.
export const IS_AGENT_ENABLED = 'ff.is_agent_enabled'
// Keeps a hub session's chat open while a workflow drives it, so the advisor
// can talk to the session's agent as the workflow runs.
export const HUB_COMPANION_DRIVE = 'ff.hub_companion_drive'
// Lets a viewer toggle the agent chat's debug overlays (turn actor/outcome
// strips, raw tool calls) with hotkeys. Target by user, not org.
export const AGENT_CHAT_DEBUG = 'ff.frontend_platform.agent_chat_debug'
// On lets users edit quantities in the AI chat proposal summary's Actions
// table; off keeps them read-only.
export const IS_PROPOSAL_SUMMARY_QUANTITY_EDIT_ENABLED =
  'ff.frontend_platform.proposal_summary_quantity_edit'
// Shows the Start account setup and Start cash raise buttons on the Workflows
// view. The viewer also needs WORKFLOWS_VIEW and `workflow::read`.
export const START_ACCOUNT_SETUP = 'ff.frontend_platform.start_account_setup'

// Support
export const IS_CHANGELOG_WIDGET_ENABLED =
  'ff.frontend_platform.changelog_widget'
export const DISABLE_PYLON_CHAT = 'disable_pylon_chat'

// User Management
// Add/Edit User dialog saves submit only the fields the admin actually
// edited, routing through the atlas PATCH when no account-access fields
// changed. Off = legacy behavior: admins with account::read always submit
// every account field through the account-management endpoints.
export const ENABLE_USER_EDITED_FIELD_SAVES =
  'ff.frontend_platform.users.edited_field_saves'

// Admin
export const REP_FEE_EDITOR_ENABLED = 'ff.rep_fee_editor_enabled'

// Clients
export const HELD_AWAY_ACCOUNTS = 'ff.held_away_accounts'
// Gates the "Pending sells" row in the account-details Cash card.
export const IS_PENDING_SELLS_CASH_ROW_ENABLED =
  'ff.frontend_platform.pending_sells_cash_row_enabled'
// Hides sleeve and cash-management UI on non-UMA accounts.
export const HIDE_NON_UMA_CASH_UI =
  'ff.frontend_platform.account_details.hide_non_uma_cash'
// Marketplace
export const MARKETPLACE = 'ff.frontend_platform.marketplace'

// Instruments
export const INSTRUMENT_DETAILS_FE = 'ff.frontend_platform.instrument_details'

// IPOs
export const IPOS_STANDALONE_UI = 'ff.ipos.standalone_ui'

// Education
export const EDUCATION_FRONTEND_FEATURE = 'ff.education_frontend_feature'

// Adding 'FF: TRADING_LAUNCH_CONFIG for easy grepping of uses in code
export const TRADING_LAUNCH_CONFIG = 'trading_launch'

// Dynamic config for objective parameters in optimizer
export const OBJECTIVE_PARAMS_CONFIG = 'objective_params'

// Dynamic config for optimization default parameters (optimizer params)
export const OPTIMIZATION_DEFAULT_PARAMETERS = 'optimization_default_parameters'

// Per-org proposal PDF branding, shared with the reporter service.
export const REPORTER_COVER_BRANDING_CONFIG = 'reporter_cover_branding'

// Names of remote (dynamic) configs — everything else is a boolean feature gate.
// Keep in sync when adding new `useRemoteConfig` consumers.
export const REMOTE_CONFIG_NAMES = [
  TRADING_LAUNCH_CONFIG,
  OBJECTIVE_PARAMS_CONFIG,
  OPTIMIZATION_DEFAULT_PARAMETERS,
  REPORTER_COVER_BRANDING_CONFIG,
] as const

// Explicit roster of boolean feature gates. The devtools dialog iterates this
// tuple; adding a new gate requires touching this list so stray string
// exports (labels, URLs, enum members) don't get silently evaluated as flags.
export const FEATURE_GATE_NAMES = [
  IS_ATTESTATION_AUTHORING_ENABLED,
  IS_MARKETPLACE_RESTRICTION_AUTHORING_ENABLED,
  IS_EXTERNAL_QUOTES_UPLOAD_ENABLED,
  // Quant
  IS_STRATEGIES_ENABLED,
  IS_SECURITY_LIST_UPLOAD_VIA_SORM_ENABLED,
  IS_INSTRUMENT_SEARCH_VIA_SORM_ENABLED,
  IS_PORTFOLIOS_REBALANCING_V2_ENABLED,
  HOLDINGS_UPLOAD_NEW_MONEY_MODE,
  LINK_ACCOUNTS_PROPOSAL_BUILDER,
  PROPOSAL_BUILDER_V2_SHOW_MUNICIPAL_ATTRIBUTES,
  PORTFOLIO_BUILDER_MAX_SELL_CONSTRAINT,
  IS_PORTFOLIO_ACCOUNT_VIEW_ENABLED,
  HIDE_ALLOCATION_TOOL,
  ENABLE_AOMS_RISK_GROUP_MISMATCH_ENFORCEMENT,
  IS_PROJECTED_CASH_PIVOT_ENABLED,
  IS_PROPOSAL_VERSION_HISTORY_ENABLED,
  IS_HELP_CENTER_ENABLED,
  HELP_CENTER_TB2_ARTICLE,
  ATTESTATION_ENFORCEMENT_ENABLED,
  IS_AI_ENABLED,
  IS_AI_OPUS_FAST_ENABLED,
  SHOW_AI_CONTROLS,
  HIDE_PROPOSAL_LIST_PAGE,
  IS_PROPOSAL_CURRENCY_SELECTOR_ENABLED,
  DISABLE_SECURITY_LEVEL_SLEEVE_ASSIGNMENT,
  PROPOSAL_BUILDER_EXPORT_FOR_PORTFOLIO_SOLUTIONS,
  IS_VIEWS_LOT_EDITING_ENABLED,
  IS_VIEWS_DRAG_REORDER_ENABLED,
  ENABLE_VIEWS_SORM_GRID,
  ENABLE_ACCOUNT_PROPOSALS_SORM_GRID,
  IS_EXPORT_EFFECTIVE_MARKUP_TO_CSV_ENABLED,
  MOA_CRR_SHOW_WITHDRAWN,
  IS_STRATEGY_ENTITY_ENABLED,
  IS_ALLOCATION_TEMPLATE_ENTITY_ENABLED,
  IS_ALLOCATION_TEMPLATE_ACCOUNT_ASSIGNMENT_ENABLED,
  IS_SLEEVE_ALLOCATION_VALIDATION_ENABLED,
  IS_ACCOUNT_GROUP_ENTITY_ENABLED,
  IS_EXPORT_BESPOKE_SUMMARY_ENABLED,
  ENABLE_BULK_PROPOSAL_ACTIONS,
  ENABLE_TRADE_PLAN_OPTIMIZATION,
  IS_MINIMIZE_DEPLOYED_CASH_OPTIMIZATIONS_ENABLED,
  ENABLE_PROPOSAL_CUSTOM_FIELDS,
  HOUSEHOLD_OPTIMIZATION,
  // Reports
  REPORTS_V2,
  // Trading
  ENABLE_TRADER_ORDER_BUYING_POWER_CHECKS,
  VIEW_UNMATCHED_EXECUTIONS_ENABLED,
  USER_SETS_HOMEPAGE,
  ENABLE_IPO_IOI_ORDER_MANAGEMENT,
  ENABLE_EXECUTION_PRINCIPAL_ACCOUNT_SELECT,
  ENABLE_LOB_SOR_EXECUTION,
  ENABLE_SIMPLIFIED_ORDER_ENTRY,
  ENABLE_MARGIN_TRADING,
  ENABLE_PRIME_BROKERAGE_OVERRIDE,
  ENABLE_AUTO_EX_RFQ_DESK_FEES,
  ENABLE_POST_TRADE_ALLOCATION,
  ENABLE_BLOTTER_V2,
  DISABLE_BLOTTER_V1,
  ENABLE_BLOTTER_ORDER_ALERTS,
  DISABLE_FORCED_STRIPED_ROWS,
  ENABLE_FIFO_WITH_AVERAGING,
  ENABLE_REP_FEES,
  ENABLE_EQUITIES_TRADING_UI,
  ENABLE_EXECUTION_ORDER_REPLACE,
  ENABLE_TRADE_CANCEL,
  ENABLE_ACCOUNT_DETAILS_HOLDINGS_FUND_TRADING,
  ENABLE_NEW_ACCOUNT_DETAILS_HOLDINGS_ORDER_ENTRY,
  ENABLE_EQUITY_UNIVERSE_CONTROLS,
  USE_MKTX_RFQ_TIMER_LOOKUP,
  ENABLE_ADVISOR_QUOTE_WANTED,
  ENABLE_ORDER_EXECUTION_BEST_QUOTE,
  ENABLE_INQUIRIES,
  ENABLE_BULK_UPLOAD_V2,
  HIDE_FULL_PAGE_ORDER_ENTRY,
  ENABLE_BULK_SEND_LIST_RFQS,
  ENABLE_TB2_LAUNCH_CALLOUT,
  ENABLE_OPCO_RESEARCH_TRADE_MODE,
  TRADING_GROUP_OBJECT_ACCESS,
  ENABLE_ORDER_INSTRUMENT_PANE,
  // Support
  IS_CHANGELOG_WIDGET_ENABLED,
  DISABLE_PYLON_CHAT,
  // User Management
  ENABLE_USER_EDITED_FIELD_SAVES,
  // Admin
  REP_FEE_EDITOR_ENABLED,
  // Clients
  HELD_AWAY_ACCOUNTS,
  IS_PENDING_SELLS_CASH_ROW_ENABLED,
  HIDE_NON_UMA_CASH_UI,
  // Marketplace
  MARKETPLACE,
  ENABLE_SECURITIZED_VIEW,
  // Instruments
  INSTRUMENT_DETAILS_FE,
  // IPOs
  IPOS_STANDALONE_UI,
  // Education
  EDUCATION_FRONTEND_FEATURE,
  // Research
  RESEARCH_FE,
  FIXED_INCOME_HUB,
  WORKFLOWS_VIEW,
  IS_AGENT_ENABLED,
  HUB_COMPANION_DRIVE,
  AGENT_CHAT_DEBUG,
  IS_PROPOSAL_SUMMARY_QUANTITY_EDIT_ENABLED,
  START_ACCOUNT_SETUP,
] as const

export enum RequiredFormFieldsForOrg {
  HasDiscretionaryAuthority = 'hasDiscretionaryAuthority',
  IsSolicited = 'isSolicited',
  AdvisorName = 'advisorName',
  AdvisorEmail = 'advisorEmail',
}

// !!NOTE!!: This must be kept in sync with the Statsig config
// Currently not a great way to do this w/o manual oversight
// https://console.statsig.com/2bDVSXL05BnhveeeM4PkWe/dynamic_configs/trading_launch
export interface TradingLaunchConfig {
  bulkUploadTemplateKey: BulkUploadTemplates
  bulkUploadTemplateKeyWithEditChecks: BulkUploadTemplates | null
  bulkUploadTemplateKeyLplStreetTrades: BulkUploadTemplates | null
  requiredFormFieldsForOrg: RequiredFormFieldsForOrg[]
  riskGroupsMappings: RiskGroupsMappings
  defaultRfqType: ValidRfqType
  // Pre-selects a venue by its MPID (e.g. "MKTX", "BMBW") for BOTH the List RFQ
  // modal and single-order RFQs. Honored only when the venue is eligible for
  // the RFQ; otherwise (or when null) each surface falls back to its house
  // priority / first supported venue.
  // NOTE: the `ListRfq` in the name is historical — this began as a List-RFQ-only
  // setting and later grew to seed single RFQs too. The name is kept for config
  // compatibility despite now covering both surfaces.
  defaultListRfqVenueMpid: string | null
  // Use a dynamic config value to know which custodians we should show the algo
  // trigger during rollout of this feature
  // TODO (kwame): once we have rolled algos for all custodians make this dynamic
  // config value be a bool for supported so custodian gating is done in the endpoint
  algoEnabledCustodians?: string[]
  // Custodians for which the execution order update (replace) action is shown.
  // TODO (kwame): once we have rolled updates for all custodians, gate if should show
  // updates on the custodian endpoint.
  executionOrderReplaceEnabledCustodians?: string[]
  // Grouping criteria that start checked in the blotter v2 Group orders flow,
  // as SORM Order column keys (e.g. ["order_type"] leaves Custodian unchecked).
  // Side, Security and Settlement date are always on. Absent means every
  // criterion starts checked.
  defaultGroupingCriteria?: string[]

  // Boolean flags
  // Enables margin-trading UI. OR-combined with the ENABLE_MARGIN_TRADING gate
  // during the migration off the boolean flag; keep in sync with the Statsig config.
  hasMarginTrading: boolean
  isBulkUploadEnabled: boolean
  isAdvisorBulkUploadEnabled: boolean
  isGroupingOrdersEnabled: boolean
  isLegacyOrderEntryEnabled: boolean
  isStreamlinedOrderTicketEnabled: boolean
  showPrimeBrokerageColumn: boolean
  // Shows the trade blotter Trader column as initials only (e.g. "JD") instead
  // of the full trader name, to save horizontal space.
  showTraderInitialsOnBlotter: boolean
  // Allows Group Parent Orders to combine allocations across different
  // custodians (matching only on CUSIP/side/settlement date) instead of
  // requiring every order in the group to share a custodian. Equity GPO
  // sessions ignore this and never cross custodians.
  enableCrossCustodianGpoGrouping: boolean
  disableBlockTrading: boolean
  shouldSeeOrderTypeToggle: boolean
  useExportTradeBlotterAllocations: boolean
  // When true AND useExportTradeBlotterAllocations is also true, the Export
  // allocations button is confined to the Historical Orders pages and hidden on
  // the current-day blotters (Trader Trade Blotter + Advisor Orders). False or
  // absent preserves the useExportTradeBlotterAllocations behavior (button shown
  // everywhere). Has no effect when useExportTradeBlotterAllocations is false —
  // that master flag still hides the button on every surface.
  hideCurrentDayBlotterExport: boolean
  isBypassStreetSideBookingEnabled: boolean
  allowMultiCustodialOrdersInBulkUpload: boolean
  // Gates access to the List RFQs feature (the blotter button, column, and
  // modal). Which venues appear within the modal (MKTX, BMBW) is driven by the
  // org's venue configurations, not this flag.
  canUseListRfqs: boolean
  // Bypasses the advisor blotter's account-access filtering, showing all
  // orders regardless of account access (same as the trader view). Used to
  // target orgs whose users lack accounts in our system but need Advisor-tier
  // (not Trader-tier) blotter access.
  skipAdvisorBlotterOrderFiltering: boolean

  // Derived values
  isMultiFlowBulkUploadEnabled: boolean

  // Account warning suppression
  suppressOrderEntryAccountWarnings: boolean
}

// !!NOTE!!: This must be kept in sync with the Statsig config
// Configure in Statsig console under dynamic_configs/objective_params
interface ObjectiveParamsConfig extends ObjectiveConfig {
  // ObjectiveConfig includes:
  // - objective_strategy?: ObjectiveStrategy
  // - buy_objective_terms?: ObjectiveTerm[]
  // - sell_objective_terms?: ObjectiveTerm[]
}

interface OptimizationDefaultParametersConfig {
  use_evals_as_orders?: boolean
}

interface ReporterCoverBrandingConfig {
  prepared_by?: string
}

// Register remote-config payload shapes so `useRemoteConfig(NAME)` infers the
// return type instead of requiring `as unknown as X` at each call site.
declare module '@moment/app-config-ui' {
  interface RemoteConfigMap {
    trading_launch: TradingLaunchConfig
    objective_params: ObjectiveParamsConfig
    optimization_default_parameters: OptimizationDefaultParametersConfig
    reporter_cover_branding: ReporterCoverBrandingConfig
  }
}
